import {
  ChangeDetectionStrategy,
  Component,
  WritableSignal,
  signal,
  ViewChild,
  effect,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import {
  DxButtonModule,
  DxFormModule,
  DxLoadPanelModule,
  DxNumberBoxModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxCheckBoxModule,
  DxValidatorModule,
  DxValidationGroupModule,
  DxValidationSummaryModule,
  DxFormComponent,
  DxLoadIndicatorModule,
} from 'devextreme-angular';
import {
  DriverState,
  NotificationState,
  ProcessingState,
  ProjectDto,
  UpdateProjectData,
  UserData,
} from '../../shared/generated/backend';
import { DriverDto, FleetService, ProjectService, VehicleDto } from '../../shared/generated/backend';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../shared/shared.module';

import { VehicleScheduleRefreshService } from '../../services/vehicle-schedule-refresh.service';
import { NotificationService } from '../../shared/services/notification.service';
import notify from 'devextreme/ui/notify';
import { OptionChangedEvent } from 'devextreme/ui/select_box';
import { ValueChangedEvent } from 'devextreme/ui/date_box';
import { firstValueFrom } from 'rxjs';
import { PlanningHelperService } from '../../services/planning-helper.service';
import { SessionService } from '../../shared/services/session.service';
import { format } from 'date-fns';

@Component({
  selector: 'app-project-detail',
  imports: [
    SharedModule,
    DxFormModule,
    DxSelectBoxModule,
    DxButtonModule,
    DxTextBoxModule,
    DxNumberBoxModule,
    DxLoadPanelModule,
    DxValidationGroupModule,
    DxCheckBoxModule,
    DxValidatorModule,
    DxValidationSummaryModule,
    DxLoadIndicatorModule,
  ],
  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectDetailComponent implements OnInit {
  readonly changed = output<string>();
  readonly project = input.required<WritableSignal<ProjectDto>>();
  @ViewChild(DxFormComponent, { static: false }) form!: DxFormComponent;
  stateChanged: boolean = false;
  isDirty: boolean = false;
  isLoading: boolean = true;

  VehicleList: WritableSignal<VehicleDto[]> = signal([]);
  DriverList: WritableSignal<DriverDto[]> = signal([]);
  AdminList: WritableSignal<UserData[]> = signal([]);
  ProcessingStateList: { display: string; value: string }[] = [];
  DriverStateList: { display: string; value: string }[] = [];
  NotificationStateList: { display: string; value: string }[] = [];

  private projectService = inject(ProjectService);
  private fleetService = inject(FleetService);
  private translateService = inject(TranslateService);
  private vehicleScheduleRefreshService = inject(VehicleScheduleRefreshService);
  private planningHelperService = inject(PlanningHelperService);
  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);

  /**
   * ProcessingState as last loaded from the server. The form edits project()() in place, so
   * project()().ProcessingState already holds an unsaved selection; all edit permissions must be
   * based on the persisted state instead, otherwise picking a state locks fields before saving.
   */
  private readonly savedState = signal<ProcessingState | undefined>(undefined);

  constructor() {
    effect(async () => {
      await this.loadSelectionData();
    });
    // every (re)load sets a new project object; in-form edits do not, so this only tracks saved data
    effect(() => {
      const state = this.project()().ProcessingState;
      this.savedState.set(state);
      this.ProcessingStateList = this.buildProcessingStateList(state);
    });
  }

  private persistedState(): ProcessingState {
    return this.savedState() ?? this.project()().ProcessingState;
  }

  private buildProcessingStateList(currentState: ProcessingState): { display: string; value: string }[] {
    const validStates: ProcessingState[] = [
      currentState, // current state is always "valid"
      ProcessingState.TransportationScheduling,
      ProcessingState.TransportationSchedulingDone,
      ProcessingState.Approved,
    ];
    // If ProcessingState is a numeric enum, this keeps only the string entries.
    const states = Object.values(ProcessingState).filter((s): s is ProcessingState => typeof s === 'string');
    return states
      .filter(state => this.sessionService.isAdmin || validStates.includes(state))
      .map(state => ({
        value: state,
        display: this.translateService.instant(`PROCESSING_STATE.${state}`),
      }));
  }

  ngOnInit() {
    this.DriverStateList = Object.values(DriverState).map(value => ({
      value: value,
      display: this.translateService.instant(`DRIVER_STATE.${value}`),
    }));

    this.NotificationStateList = Object.values(NotificationState).map(value => ({
      value: value,
      display: this.translateService.instant(`NOTIFICATION_STATE.${value}`),
    }));

    this.planningHelperService.requestOmCheck(this.project()().ProjectNo!);
  }

  isRampNeeded: boolean = false;

  tryChangeDriver = (e: OptionChangedEvent) => {
    if (!('event' in e) || e['event'] == undefined) return; // the event is not triggered by an option change
    const newVehicleId = e.value;
    if (!newVehicleId) return;
    const newVehicle = this.VehicleList().find(vehicle => vehicle.vehicleId === newVehicleId);
    if (!newVehicle) return;
    // special case: courier vehicle
    if (newVehicle.isCourier || newVehicle.isExternal) {
      this.project()().DriverId = 0;
      this.project()().Driver = 'Kurier';
      return;
    }
    this.project()().RampNeeded ||= newVehicle.hasRamp!;
    const newDriverId = newVehicle.employeeNumber;
    const newDriver = this.DriverList().find(driver => driver.employeeNumber === newDriverId);
    if (!newDriver)
      this.project()().DriverId = undefined; // if no matching driver is found, clear the driver
    else this.project()().DriverId = newDriver.employeeNumber; // set the default driver of the vehicle
  };

  hasRoleStatus(): boolean {
    return this.sessionService.isAdmin || this.sessionService.isDispo;
  }

  loadSelectionData = async (e?: ValueChangedEvent) => {
    if (e != undefined) {
      if (!('event' in e) || e['event'] == undefined) return; // the event is not triggered by an option change
    }
    try {
      this.isLoading = true;
      const VehicleList = await firstValueFrom(
        this.fleetService.getAvailableVehicles(
          format(this.project()().FromDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          format(this.project()().UntilDateTime, "yyyy-MM-dd'T'HH:mm:ss")
        )
      );

      // if not already included, include the current vehicle
      const project = this.project();
      if (!VehicleList.find(x => x.vehicleId == this.project()().VehicleId)) {
        VehicleList.push({
          vehicleId: project().VehicleId!,
          identifier: project().Vehicle,
          displayName: project().Vehicle!,
          isCourier: project().isCourier,
          isExternal: false,
          hasRamp: false,
        });
      }
      this.VehicleList.set(VehicleList);

      const driverList = await firstValueFrom(
        this.fleetService.getAvailableDrivers(
          format(project().FromDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          format(project().UntilDateTime, "yyyy-MM-dd'T'HH:mm:ss")
        )
      );
      if (!driverList.find(x => x.employeeNumber == this.project()().DriverId)) {
        driverList.push({ employeeNumber: project().DriverId!, identifier: project().Driver });
      }
      driverList.sort((a, b) => (a.identifier ?? '').localeCompare(b.identifier ?? ''));
      this.DriverList.set(driverList);

      const adminList = await firstValueFrom(this.projectService.getAdminSelection());
      this.AdminList.set(adminList);
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading selection data', error);
      this.requestReload();
    }
  };

  onRefreshClicked() {
    this.requestReload();
  }

  allowEdit = (): boolean => {
    return this.persistedState() == ProcessingState.TransportationScheduling && !this.isLoading;
  };

  allowUntilDateChange = (): boolean => {
    const correctState = [
      ProcessingState.TransportationScheduling,
      ProcessingState.TransportationSchedulingDone,
      ProcessingState.Approved,
      ProcessingState.ProcessingByWarehouse,
      ProcessingState.FixedByWarehouse,
      ProcessingState.Rolling,
    ].includes(this.persistedState());
    return !this.isLoading && correctState;
  };

  // not loading, has role status, not completed. The state may be changed repeatedly and together
  // with other fields; everything is applied on save.
  allowStateChange = (): boolean => {
    return (
      !this.isLoading &&
      this.hasRoleStatus() &&
      ([
        ProcessingState.TransportationScheduling,
        ProcessingState.TransportationSchedulingDone,
        ProcessingState.Approved,
      ].includes(this.persistedState()) ||
        this.sessionService.isAdmin)
    );
  };

  allowVehicleChange = (): boolean => {
    return this.project()().DriverState! == DriverState.NotDispatched && this.allowEdit();
  };

  allowDriverChange = (): boolean => {
    return !this.courierVehicleSelected() && this.allowVehicleChange();
  };

  courierVehicleSelected = (): boolean => {
    const selectedVehicle = this.VehicleList().find(vehicle => vehicle.vehicleId === this.project()().VehicleId);
    return (selectedVehicle?.isCourier || selectedVehicle?.isExternal) ?? false;
  };

  discardChanges() {
    this.form.instance.reset();
    this.requestReload();
  }

  onFormSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    if (!this.isDirty) return;
    const validationRes = this.form.instance.validate();
    if (!validationRes.isValid) {
      return;
    }
    const updateData: UpdateProjectData = {
      ProcessingState: this.project()().ProcessingState!,
      NotificationSend: this.project()().NotificationSend!,
      FromDateTime: this.project()().FromDateTime,
      UntilDateTime: this.project()().UntilDateTime,
      VehicleId: this.project()().VehicleId ?? 0, // TODO: this can be null
      DriverId: this.project()().DriverId ?? 0,
      Priority: this.project()().Priority,
      RampNeeded: this.project()().RampNeeded,
      TunnelCode: this.project()().TunnelCode ? 1 : 0,
      ProjectText: this.project()().ProjectText,
      DriverText: this.project()().DriverText,
      StockText: this.project()().StockText,
      Administrator: this.project()().Administrator,
    };
    this.isLoading = true;
    this.isDirty = false;
    this.projectService.updateProject(this.project()().ProjectNo!, updateData).subscribe({
      next: () => {
        this.vehicleScheduleRefreshService.triggerRefresh();
        this.form.instance.reset();
        this.requestReload();
        this.isLoading = false;
      },
      error: err => {
        console.error(err);
        if (typeof err.error === 'string') {
          notify(this.translateService.instant('ERROR.' + err.error), 'error', 3000);
        } else if (Array.isArray(err.error)) {
          const message = this.translateService.instant('PROJECT_PLANNING.OM_CHECK') + err.error.join(', ');
          notify(message, 'warning', 6000);
          this.planningHelperService.requestOmCheck(this.project()().ProjectNo!);
        }
        this.requestReload();
      },
    });
  };

  runValidation() {
    if (this.form?.instance != null) {
      try {
        this.form.instance.validate();
      } catch (error) {
        console.warn('Validation group not ready yet', error);
      }
    } else {
      console.error('no form instance');
    }
  }

  validateDriver = (e: { value: string; validator: object }): boolean => {
    return (
      this.isLoading ||
      this.courierVehicleSelected() ||
      this.DriverList().some(driver => driver.employeeNumber === Number(e.value))
    );
  };

  validateVehicle = (e: { value: string }): boolean => {
    return this.isLoading || this.VehicleList().some(vehicle => vehicle.vehicleId === Number(e.value));
  };

  validateFromDate = (e: { value: string }): boolean => {
    const currentDate = new Date(this.project()().FromDateTime!);
    const newDate = new Date(e.value);
    return this.isLoading || newDate <= currentDate;
  };

  validateUntilDate = (e: { value: string }): boolean => {
    const newDate = new Date(e.value);
    const currentDate = new Date(this.project()().FromDateTime!);
    return this.isLoading || newDate >= currentDate;
  };

  requestReload = () => {
    this.isDirty = false;
    this.changed.emit('reload');
    this.notificationService.triggerRefresh();
  };
}
